import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, Play, PlugZap, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { refreshAdminSettings } from "@/lib/useAdminSettings";
import { PageHeader, Panel } from "@/components/admin/common";

const RULES = [
  ["auto_reminders", "Payment reminders", "7 days and 3 days before travel, when a balance is due", "reminder"],
  ["auto_briefings", "Trip briefings", "2 days before travel, when pickup & ferry details are added", "briefing"],
  ["auto_feedback", "Feedback requests", "1 day after the trip ends, with your Google review link", "feedback"],
];

const STEPS = [
  "Go to developers.facebook.com, create an app and choose 'Connect with customers through WhatsApp'. Create or select your Meta Business Portfolio.",
  "In WhatsApp Manager add +919548340299, verify it by SMS or call, then register it. If the number is in the WhatsApp / WhatsApp Business app, delete that account from the app first.",
  "Complete Meta business verification for Ocean Wings Travel (Business Settings → Security Centre).",
  "Create the message templates below in WhatsApp Manager → Message templates (language: English) and wait until each shows 'Approved'. The follow-up digest template is sent only to your own alert number (Company Settings → Lead follow-ups).",
  "In Business Settings → Users → System users, create a system user, assign the app and WhatsApp account, and generate a permanent token with whatsapp_business_messaging and whatsapp_business_management permissions.",
  "Copy the Phone number ID (from WhatsApp → API Setup, not the phone number itself) and the permanent token, and share them with me. I will connect them securely.",
];

const Templates = ({ templates }) => (
  <div className="space-y-3">
    {Object.values(templates).map((t) => (
      <div key={t.name} className="rounded-xl border border-slate-200 p-4" data-testid={`wa-template-${t.name}`}>
        <div className="flex flex-wrap items-center gap-2"><p className="font-mono text-sm font-semibold text-navy">{t.name}</p><span className="text-xs rounded-full bg-sky-50 text-sky-800 px-2 py-0.5">{t.category}</span></div>
        <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap break-words">{t.body}</p>
        <p className="mt-2 text-xs text-slate-500 break-words">Sample values: {t.examples.map((e, i) => `{{${i + 1}}} ${e}`).join(" · ")}</p>
      </div>
    ))}
  </div>
);

export default function Automation() {
  const [st, setSt] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/whatsapp/status").then((r) => setSt(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, value) => {
    try {
      const { data: s } = await api.get("/admin/settings");
      const { data } = await api.put("/admin/settings", { ...s, [key]: value });
      refreshAdminSettings(data);
      setSt((x) => ({ ...x, [key]: value }));
      toast.success("Automation updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const run = async () => {
    setBusy("run");
    try { await api.post("/admin/whatsapp/run"); toast.success("Daily run started"); setTimeout(load, 2500); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post("/admin/whatsapp/test", { to }); data.state === "accepted" ? toast.success("Test message accepted by WhatsApp") : toast.error(data.error || "Test failed"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  if (!st) return <Skeleton className="h-64 rounded-2xl" />;
  const last = st.runs[0];
  return (
    <div data-testid="admin-automation-page">
      <PageHeader title="WhatsApp automation" subtitle="Messages go out automatically every day at 9:00 AM IST through the WhatsApp Business (Meta Cloud) API." />
      <Panel className={`p-5 mb-4 flex items-start gap-4 ${st.connected ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="wa-connection-status">
        {st.connected ? <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" /> : <PlugZap className="h-6 w-6 text-amber-600 shrink-0" />}
        <div className="text-sm">
          <p className="font-semibold text-navy">{st.connected ? "WhatsApp Business connected" : "WhatsApp Business not connected yet"}</p>
          <p className="text-slate-600 mt-0.5">{st.connected ? "Automatic messages are active for the rules switched on below." : "Nothing is sent automatically until your Meta account is connected. Follow the setup steps below. Manual one-tap sending keeps working meanwhile."}</p>
          {last && <p className="text-xs text-slate-500 mt-2" data-testid="wa-last-run">Last run {fmtDate(last.started_at)} ({last.trigger}): {last.status === "done" ? `${last.sent || 0} sent, ${last.failed || 0} failed${last.skipped?.length ? ` · ${last.skipped.join(" · ")}` : ""}` : last.status}</p>}
        </div>
      </Panel>
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel className="p-6 min-w-0">
          <p className="font-semibold text-navy">Automatic rules</p>
          <div className="mt-4 divide-y divide-slate-100">
            {RULES.map(([k, label, hint]) => (
              <div key={k} className="py-3 flex items-center justify-between gap-4">
                <div><p className="text-sm font-medium text-navy">{label}</p><p className="text-xs text-slate-500">{hint}</p></div>
                <Switch checked={!!st[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`wa-toggle-${k}`} />
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Your WhatsApp number for a test" data-testid="wa-test-number-input" />
            <Button variant="outline" disabled={!st.connected || !to || busy === "test"} onClick={test} data-testid="wa-test-button">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" />Send test</>}</Button>
            <Button className="bg-ocean hover:bg-navy" disabled={busy === "run"} onClick={run} data-testid="wa-run-now-button">{busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-1.5" />Run now</>}</Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">"Run now" does today's 9 AM job immediately. A guest never gets the same message twice on the same day.</p>
        </Panel>
        <Panel className="p-6 min-w-0">
          <p className="font-semibold text-navy">Setup steps (one time)</p>
          <ol className="mt-4 space-y-3 text-sm text-slate-700 list-decimal pl-5" data-testid="wa-setup-steps">{STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
        </Panel>
      </div>
      <Panel className="p-6 mt-6 min-w-0">
        <p className="font-semibold text-navy">Message templates to create in Meta</p>
        <p className="text-xs text-slate-500 mb-4">Copy each name and body exactly. Meta must approve them before automatic sending works.</p>
        <Templates templates={st.templates} />
      </Panel>
      <Panel className="p-6 mt-6 min-w-0">
        <p className="font-semibold text-navy">Recent automatic messages</p>
        {st.messages.length === 0 ? <p className="mt-3 text-sm text-slate-500" data-testid="wa-messages-empty">No messages sent yet.</p> : (
          <div className="mt-3 divide-y divide-slate-100" data-testid="wa-messages-list">
            {st.messages.map((m, i) => (
              <div key={i} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="text-xs text-slate-500 w-24">{fmtDate(m.created_at)}</span>
                <span className="font-medium text-navy">{m.customer_name}</span><span className="text-xs text-slate-500">{m.booking_no} · {m.template}</span>
                <span className={`ml-auto text-xs font-semibold ${m.state === "accepted" ? "text-emerald-600" : "text-rose-600"}`}>{m.state}{m.error ? `: ${m.error}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
